/**
 * The Shortcut_Handler's binding table and dispatcher (Requirement 3).
 *
 * One declarative table, {@link SHORTCUT_BINDINGS}, is the whole of it. It is the
 * dispatch source *and* the data source for the searchable cheat sheet
 * (Requirement 3.7), which is the point: a second list of shortcuts written for the
 * help panel would be wrong within a week, and the requirement asks for a list of
 * every combination the handler defines, not a list someone maintains beside it.
 *
 * How dispatch works:
 *
 * 1. {@link comboOf} normalizes a keyboard event into a canonical combo string —
 *    modifiers in a fixed order (`Ctrl+Alt+Shift+Key`), `Meta` folded into `Ctrl`
 *    so a Mac's Command key works, letters upper-cased, the space bar spelled
 *    `Space`.
 * 2. {@link findBinding} looks the combo up in an index keyed by combo *and* mode.
 *    Mode awareness is what lets `Alt+Enter` insert a line break while editing and
 *    `Ctrl+Enter` fill the selection, without either firing in the grid.
 * 3. {@link handleKey} calls `preventDefault()` on every match and only on a match
 *    (Requirement 3.6). `Ctrl+S`, `Ctrl+F`, `Ctrl+P`-adjacent combos, and `Tab` all
 *    have browser defaults that would otherwise take the page away from the user;
 *    suppression is the binding table's job rather than a blanket
 *    `preventDefault()` on every key, which would break `Ctrl+Shift+I` and the
 *    browser's own accessibility keys.
 *
 * The index is built once at module load and throws on a duplicate combo/mode pair,
 * so an ambiguous table cannot ship even if nobody runs the property test.
 *
 * This module is pure and DOM-free: it takes {@link KeyEventLike}, which a real
 * `KeyboardEvent` satisfies structurally. What each action *does* is the caller's
 * business — `src/ui/gridView.ts` handles the ones that move the selection or open
 * the editor and forwards the rest to the App Shell.
 *
 * **What is deliberately not in the table.** Plain arrow keys, `PageUp`/`PageDown`,
 * and unmodified `Home`/`End` move the *viewport* and belong to
 * `interaction.navigate`; the autocomplete list's own `ArrowUp`/`ArrowDown`/`Tab`
 * belong to the listbox widget, which consumes them before the dispatcher sees
 * them. Binding either group here would suppress caret movement inside the editor,
 * and a table that claims a key it should not claim is worse than a shorter table.
 */

import type { Direction } from '../core/types.js';
import type { CommitKey } from './editor.js';

/** Where a key press is going (design: `handle(e, mode)`). */
export type ShortcutMode = 'grid' | 'edit' | 'dialog';

/** Every command the table maps to. */
export type ShortcutAction =
  // Requirement 3.1, clipboard and history
  | 'copy'
  | 'cut'
  | 'paste'
  | 'undo'
  | 'redo'
  | 'save'
  /**
   * `Ctrl+P`. Not named by Requirement 3.1, but claimed anyway: the browser's own
   * print dialog would print the DOM, and the DOM of this app is a canvas plus a
   * toolbar — a blank page as far as a user's data is concerned. Claiming the combo
   * suppresses that (Requirement 3.6) and reports the action as unavailable until a
   * print subsystem exists, which is a better answer than a blank sheet of paper.
   */
  | 'print'
  | 'find'
  | 'replace'
  | 'selectAll'
  | 'toggleBold'
  | 'toggleItalic'
  | 'toggleUnderline'
  | 'formatDialog'
  | 'fillDown'
  | 'fillRight'
  | 'insertDate'
  | 'toggleFilters'
  | 'selectColumn'
  | 'selectRow'
  | 'nextSheet'
  | 'prevSheet'
  | 'editCell'
  | 'cycleAnchor'
  | 'lineBreak'
  | 'fillSelection'
  // Editing (Requirements 2.2, 2.3, 2.4)
  | 'commitDown'
  | 'commitUp'
  | 'commitRight'
  | 'commitLeft'
  | 'cancelEdit'
  // Grid movement that shares Enter and Tab with the editor
  | 'moveDown'
  | 'moveUp'
  | 'moveRight'
  | 'moveLeft'
  // Requirements 3.2, 3.3
  | 'blockUp'
  | 'blockDown'
  | 'blockLeft'
  | 'blockRight'
  | 'extendBlockUp'
  | 'extendBlockDown'
  | 'extendBlockLeft'
  | 'extendBlockRight'
  // Requirements 3.4, 3.5
  | 'homeCell'
  | 'lastCell'
  // Requirement 2.8 and the help panel of Requirement 3.7
  | 'clearValues'
  | 'insertRowAndCol'
  | 'deleteRowAndCol'
  | 'showShortcuts';

/** One row of the table: a combo, what it does, and where it applies. */
export interface KeyBinding {
  /** Canonical combo string, as {@link comboOf} produces. */
  combo: string;
  command: ShortcutAction;
  /** Human-readable description, shown in the cheat sheet. */
  label: string;
  modes: readonly ShortcutMode[];
  /** Grouping for the cheat sheet. */
  category: string;
}

const GRID: readonly ShortcutMode[] = ['grid'];
const EDIT: readonly ShortcutMode[] = ['edit'];
const BOTH: readonly ShortcutMode[] = ['grid', 'edit'];

/**
 * Every binding, in cheat-sheet order.
 *
 * Requirement 3.1's list comes first, then block navigation (3.2, 3.3), then
 * `Ctrl+Home` and `Ctrl+End` (3.4, 3.5), then the editing and movement keys that
 * make those usable. Requirement 3.7's searchable list is generated from this
 * array, so everything a user can press is here by construction.
 */
export const BINDINGS: readonly KeyBinding[] = Object.freeze([
  { combo: 'Ctrl+C', command: 'copy', label: 'Copy', modes: GRID, category: 'Clipboard' },
  { combo: 'Ctrl+X', command: 'cut', label: 'Cut', modes: GRID, category: 'Clipboard' },
  { combo: 'Ctrl+V', command: 'paste', label: 'Paste', modes: GRID, category: 'Clipboard' },
  { combo: 'Ctrl+Z', command: 'undo', label: 'Undo', modes: GRID, category: 'History' },
  { combo: 'Ctrl+Y', command: 'redo', label: 'Redo', modes: GRID, category: 'History' },
  { combo: 'Ctrl+S', command: 'save', label: 'Save workbook', modes: BOTH, category: 'Workbook' },
  { combo: 'Ctrl+P', command: 'print', label: 'Print', modes: BOTH, category: 'Workbook' },
  { combo: 'Ctrl+F', command: 'find', label: 'Find', modes: GRID, category: 'Search' },
  { combo: 'Ctrl+H', command: 'replace', label: 'Replace', modes: GRID, category: 'Search' },
  {
    combo: 'Ctrl+A',
    command: 'selectAll',
    label: 'Select all cells',
    modes: GRID,
    category: 'Selection',
  },
  {
    combo: 'Ctrl+B',
    command: 'toggleBold',
    label: 'Toggle bold',
    modes: GRID,
    category: 'Formatting',
  },
  {
    combo: 'Ctrl+I',
    command: 'toggleItalic',
    label: 'Toggle italic',
    modes: GRID,
    category: 'Formatting',
  },
  {
    combo: 'Ctrl+U',
    command: 'toggleUnderline',
    label: 'Toggle underline',
    modes: GRID,
    category: 'Formatting',
  },
  {
    combo: 'Ctrl+1',
    command: 'formatDialog',
    label: 'Open the format dialog',
    modes: GRID,
    category: 'Formatting',
  },
  { combo: 'Ctrl+D', command: 'fillDown', label: 'Fill down', modes: GRID, category: 'Fill' },
  { combo: 'Ctrl+R', command: 'fillRight', label: 'Fill right', modes: GRID, category: 'Fill' },
  {
    combo: 'Ctrl+;',
    command: 'insertDate',
    label: 'Insert the current date',
    modes: GRID,
    category: 'Editing',
  },
  {
    combo: 'Ctrl+Shift+L',
    command: 'toggleFilters',
    label: 'Toggle filters',
    modes: GRID,
    category: 'Data',
  },
  {
    combo: 'Ctrl+Shift+K',
    command: 'toggleFilters',
    label: 'Toggle filters (alt)',
    modes: GRID,
    category: 'Data',
  },
  {
    combo: 'Ctrl+Space',
    command: 'selectColumn',
    label: 'Select the current column',
    modes: GRID,
    category: 'Selection',
  },
  {
    combo: 'Shift+Space',
    command: 'selectRow',
    label: 'Select the current row',
    modes: GRID,
    category: 'Selection',
  },
  {
    combo: 'Ctrl+PageDown',
    command: 'nextSheet',
    label: 'Activate the next sheet',
    modes: GRID,
    category: 'Sheets',
  },
  {
    combo: 'Ctrl+PageUp',
    command: 'prevSheet',
    label: 'Activate the previous sheet',
    modes: GRID,
    category: 'Sheets',
  },
  {
    combo: 'F2',
    command: 'editCell',
    label: 'Edit the active cell',
    modes: GRID,
    category: 'Editing',
  },
  {
    combo: 'F4',
    command: 'cycleAnchor',
    label: 'Cycle reference anchoring',
    modes: BOTH,
    category: 'Editing',
  },
  {
    combo: 'Alt+Enter',
    command: 'lineBreak',
    label: 'Insert a line break in the cell',
    modes: EDIT,
    category: 'Editing',
  },
  {
    combo: 'Ctrl+Enter',
    command: 'fillSelection',
    label: 'Fill the selection with the edited content',
    modes: EDIT,
    category: 'Editing',
  },

  // Requirements 3.2 and 3.3: block-edge navigation.
  {
    combo: 'Ctrl+ArrowUp',
    command: 'blockUp',
    label: 'Move to the top of the data block',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Ctrl+ArrowDown',
    command: 'blockDown',
    label: 'Move to the bottom of the data block',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Ctrl+ArrowLeft',
    command: 'blockLeft',
    label: 'Move to the left edge of the data block',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Ctrl+ArrowRight',
    command: 'blockRight',
    label: 'Move to the right edge of the data block',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Ctrl+Shift+ArrowUp',
    command: 'extendBlockUp',
    label: 'Extend the selection to the top of the data block',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Ctrl+Shift+ArrowDown',
    command: 'extendBlockDown',
    label: 'Extend the selection to the bottom of the data block',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Ctrl+Shift+ArrowLeft',
    command: 'extendBlockLeft',
    label: 'Extend the selection to the left edge of the data block',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Ctrl+Shift+ArrowRight',
    command: 'extendBlockRight',
    label: 'Extend the selection to the right edge of the data block',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Ctrl+Home',
    command: 'homeCell',
    label: 'Move to cell A1',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Ctrl+End',
    command: 'lastCell',
    label: 'Move to the last used cell',
    modes: GRID,
    category: 'Navigation',
  },

  // Editing and movement: Enter and Tab mean "commit" while editing and "move"
  // in the grid, which is the clearest case for mode awareness in the table.
  {
    combo: 'Enter',
    command: 'commitDown',
    label: 'Confirm the edit and move down',
    modes: EDIT,
    category: 'Editing',
  },
  {
    combo: 'Shift+Enter',
    command: 'commitUp',
    label: 'Confirm the edit and move up',
    modes: EDIT,
    category: 'Editing',
  },
  {
    combo: 'Tab',
    command: 'commitRight',
    label: 'Confirm the edit and move right',
    modes: EDIT,
    category: 'Editing',
  },
  {
    combo: 'Shift+Tab',
    command: 'commitLeft',
    label: 'Confirm the edit and move left',
    modes: EDIT,
    category: 'Editing',
  },
  {
    combo: 'Escape',
    command: 'cancelEdit',
    label: 'Cancel the edit',
    modes: EDIT,
    category: 'Editing',
  },
  {
    combo: 'Enter',
    command: 'moveDown',
    label: 'Move down one cell',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Shift+Enter',
    command: 'moveUp',
    label: 'Move up one cell',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Tab',
    command: 'moveRight',
    label: 'Move right one cell',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Shift+Tab',
    command: 'moveLeft',
    label: 'Move left one cell',
    modes: GRID,
    category: 'Navigation',
  },
  {
    combo: 'Delete',
    command: 'clearValues',
    label: 'Clear the values of the selection',
    modes: GRID,
    category: 'Editing',
  },
  {
    combo: 'Ctrl+=',
    command: 'insertRowAndCol',
    label: 'Insert row or column at selection',
    modes: GRID,
    category: 'Editing',
  },
  {
    combo: 'Ctrl+Shift+=',
    command: 'insertRowAndCol',
    label: 'Insert row or column at selection',
    modes: GRID,
    category: 'Editing',
  },
  {
    combo: 'Ctrl+-',
    command: 'deleteRowAndCol',
    label: 'Delete selected row or column',
    modes: GRID,
    category: 'Editing',
  },
  {
    combo: 'Ctrl+/',
    command: 'showShortcuts',
    label: 'Show the keyboard shortcut list',
    modes: BOTH,
    category: 'Help',
  },
] satisfies readonly KeyBinding[]);

/** The event shape the dispatcher needs. `KeyboardEvent` satisfies it. */
export interface KeyEventLike {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  preventDefault(): void;
}

/** A combo split into its parts. */
export interface KeyChord {
  key: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
}

/**
 * The canonical name of a key.
 *
 * `' '` becomes `Space` so a combo string is readable and splittable, and single
 * letters are upper-cased because `Shift` already travels as a modifier — without
 * that, `Ctrl+Shift+L` would arrive as `Ctrl+Shift+L` on one layout and
 * `Ctrl+Shift+l` on another.
 */
export function normalizeKey(key: string): string {
  if (key === ' ' || key === 'Spacebar') return 'Space';
  if (key.length === 1) return key.toUpperCase();
  return key;
}

/** The canonical combo string of an event (`Ctrl+Alt+Shift+Key`). */
export function comboOf(event: KeyEventLike): string {
  const parts: string[] = [];
  if (event.ctrlKey || event.metaKey) parts.push('Ctrl');
  if (event.altKey) parts.push('Alt');
  if (event.shiftKey) parts.push('Shift');

  let keyStr = event.key;
  // If event provides a code like "KeyL", use "L" when key is a letter
  if ('code' in event && typeof (event as { code?: string }).code === 'string') {
    const code = (event as { code: string }).code;
    if (code.startsWith('Key')) {
      keyStr = code.substring(3);
    }
  }

  parts.push(normalizeKey(keyStr));
  return parts.join('+');
}

/** Splits a canonical combo back into its parts. Inverse of {@link comboOf}. */
export function parseCombo(combo: string): KeyChord {
  const parts = combo.split('+');
  const key = parts[parts.length - 1] ?? '';
  return {
    key: key === 'Space' ? ' ' : key,
    ctrl: parts.includes('Ctrl'),
    alt: parts.includes('Alt'),
    shift: parts.includes('Shift'),
  };
}

function indexKey(combo: string, mode: ShortcutMode): string {
  return `${mode}\u0000${combo}`;
}

/**
 * Combo + mode → binding.
 *
 * Built once, and a duplicate is a load-time error: two bindings on one combo in
 * one mode would make dispatch depend on array order, which is exactly the
 * ambiguity Property 10 rules out.
 */
const INDEX: ReadonlyMap<string, KeyBinding> = (() => {
  const map = new Map<string, KeyBinding>();
  for (const binding of BINDINGS) {
    if (binding.modes.length === 0) {
      throw new Error(`shortcut ${binding.combo} declares no mode`);
    }
    for (const mode of binding.modes) {
      const key = indexKey(binding.combo, mode);
      const clash = map.get(key);
      if (clash !== undefined) {
        throw new Error(
          `duplicate shortcut ${binding.combo} in ${mode} mode: ${clash.command} and ${binding.command}`,
        );
      }
      map.set(key, binding);
    }
  }
  return map;
})();

/** The binding for a combo in a mode, or `null`. */
export function findBinding(combo: string, mode: ShortcutMode): KeyBinding | null {
  return INDEX.get(indexKey(combo, mode)) ?? null;
}

/**
 * Dispatches one key press (Requirements 3.1, 3.6).
 *
 * On a match the browser default is suppressed and the binding is returned; on a
 * miss nothing is suppressed and `null` comes back, so the caller can fall through
 * to plain navigation or to type-to-edit.
 */
export function handleKey(event: KeyEventLike, mode: ShortcutMode): KeyBinding | null {
  const binding = findBinding(comboOf(event), mode);
  if (binding === null) return null;
  event.preventDefault();
  return binding;
}

/** Every binding available in a mode, in table order. */
export function bindingsFor(mode: ShortcutMode): KeyBinding[] {
  return BINDINGS.filter((binding) => binding.modes.includes(mode));
}

/**
 * Bindings matching a search term (Requirement 3.7).
 *
 * Matches the combo, the label, the category, and the action name, case-folded, so
 * a user can search for `bold`, `Ctrl+B`, or `formatting` and find the same row. An
 * empty query returns the whole table, which is what the cheat sheet opens with.
 */
export function searchBindings(query: string): KeyBinding[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [...BINDINGS];
  return BINDINGS.filter((binding) =>
    `${binding.combo} ${binding.label} ${binding.category} ${binding.command}`
      .toLowerCase()
      .includes(needle),
  );
}

/* ---------------------------------------------------- action interpretation */

/** A block-edge move: which way, and whether the selection extends (R3.2, R3.3). */
export interface BlockNav {
  dir: Direction;
  extend: boolean;
}

const BLOCK_NAV: Partial<Record<ShortcutAction, BlockNav>> = {
  blockUp: { dir: 'up', extend: false },
  blockDown: { dir: 'down', extend: false },
  blockLeft: { dir: 'left', extend: false },
  blockRight: { dir: 'right', extend: false },
  extendBlockUp: { dir: 'up', extend: true },
  extendBlockDown: { dir: 'down', extend: true },
  extendBlockLeft: { dir: 'left', extend: true },
  extendBlockRight: { dir: 'right', extend: true },
};

/**
 * The block-edge move an action requests, or `null`.
 *
 * A table rather than a switch, because the eight `Ctrl+Arrow` and
 * `Ctrl+Shift+Arrow` actions differ only in two fields and the mapping is the whole
 * of their meaning.
 */
export function blockNavOf(action: ShortcutAction): BlockNav | null {
  return BLOCK_NAV[action] ?? null;
}

const COMMIT_KEYS: Partial<Record<ShortcutAction, CommitKey>> = {
  commitDown: 'enter',
  commitUp: 'shiftEnter',
  commitRight: 'tab',
  commitLeft: 'shiftTab',
  fillSelection: 'ctrlEnter',
};

/** The commit an action performs, or `null` (Requirements 2.2, 2.3, 3.1). */
export function commitKeyOf(action: ShortcutAction): CommitKey | null {
  return COMMIT_KEYS[action] ?? null;
}

/**
 * True when a key press should open the editor on the active cell with that
 * character as the content (Requirement 2.1).
 *
 * One printable character, no `Ctrl` and no `Alt`: `Shift` is allowed because a
 * capital letter is a character like any other. Dead keys and IME composition
 * arrive as multi-character `key` values and are excluded, so a composed character
 * opens the editor once its `input` event lands rather than twice.
 */
export function isTypeToEdit(event: KeyEventLike): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  return event.key.length === 1 && event.key !== '\u0000';
}
